import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { newGlobalFileServer } from "../../../../axiosInstance";
import { checkout, orderPerCustomer } from "../../../server/Order";
import showAlert from "../../../server/ShowAlert";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCart,
  getCart,
  upsertCartItem,
} from "../../../server/CollectSite";
import {
  findMatchingRecentOrder,
  getOrderChargedTotal,
  isStaleCartCheckoutError,
} from "../../../utils/staleCartRecovery";
import PayPopup from "../shopCart/PayPopup";
import { getStationToCustomer } from "../../../server/Station";
import { getDeliveryPrices } from "../../../server/DeliveryPrices";
import axios from "axios";
import { awsUrl } from "../../../../axiosInstance";
import { UpdateUser } from "../../../server/Customer";
import { guardCustomerAction } from "../../../utils/emailVerification";
import { getActiveCartItems, warnEmptyCart } from "../../../utils/cartCheckout";
import {
  getCartLineUpsertPromoId,
  expandCartItemsToPackageLines,
  totalQuantityAfterRemovingPackageLine,
} from "../../../utils/packageCartLines";
import { cartRequiresAgeConfirmation } from "../../../utils/ageRestrictedProducts";
import {
  CHECKOUT_STEP,
  CHECKOUT_STEP_MIN,
  getCheckoutStepDisplayIndex,
  getCheckoutSteps,
} from "../../../constants/checkoutSteps";
import * as actionType from "../../../store/action";
import CouponCode from "./CouponCode";
import {
  clearCouponReturn,
  isCouponCheckoutReturn,
  parseCheckoutReturnStep,
} from "../../../utils/couponNavigation";
import CartShoppingListPicker from "./CartShoppingListPicker";
import html2pdf from "html2pdf.js/dist/html2pdf.bundle.min.js";
import { OrderReceiptTemplate } from "./OrderReceiptTemplate";
import ProductAddToCart from "../productPage/ProductAddToCart";
import PopUp from "../../containers/PopUp";
import ProductInfoLine from "../../productInfo/ProductInfoLine";
import { getDefaultMediaPath } from "../../../utils/productMedia";
import {
  X,
  Pencil,
  Trash2,
  Check,
  User,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  ChevronLeft,
  ListChecks,
} from "lucide-react";
import { formatAddressLines } from "../../../utils/formatAddress";
import {
  getExistingStreetCity,
  parseAddressFromPlace,
} from "../../../utils/googleAddress";
import { stripToDigits, stripToSignedInteger } from "../../../ValidFunction";
import Autocomplete from "react-google-autocomplete";
import {
  isBelowMinCheckoutCharge,
  getMinCheckoutChargeMessage,
} from "../../../constants/checkout";
import {
  CHECKOUT_ERROR_CATEGORY,
  getCheckoutErrorFallback,
  getCheckoutErrorTitle,
  resolveCheckoutError,
} from "../../../constants/checkoutErrors";
import {
  CHECKOUT_FIELD,
  CHECKOUT_VALIDATION_CATEGORY,
  validateCheckoutDetails,
} from "../../../utils/checkoutValidation";
import {
  isCheckoutRequestInFlight,
  releaseCheckoutRequest,
  tryAcquireCheckoutRequest,
} from "../../../utils/checkoutRequestGuard";

// Bound the extra order-history lookup on the stale-cart recovery path so a
// hung request can't leave the checkout stuck on the loading screen.
const STALE_CART_ORDER_LOOKUP_TIMEOUT_MS = 15000;

/* ─── Delivery pricing tiers (cart total before coupon) ─── */
// Same source as deliveryPrice: GET/PUT /carts/me response.
// Optional: deliveryPricingTiers[] when backend exposes the full tier table.

function toFiniteNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function extractDeliveryTiers(cartApiPayload) {
  const tiersArr = cartApiPayload?.deliveryPricingTiers;
  if (!Array.isArray(tiersArr) || !tiersArr.length) return null;

  const normalized = [];
  for (const item of tiersArr) {
    const minCartTotal = toFiniteNumber(item.minCartTotal);
    const price = toFiniteNumber(item.price ?? item.deliveryPrice);
    if (minCartTotal != null && price != null) {
      normalized.push({ minCartTotal, price });
    }
  }

  return normalized.length ? normalized : null;
}

function getActiveDeliveryTierIndex(cartTotal, tiersForDisplay) {
  const t = Array.isArray(tiersForDisplay) ? tiersForDisplay : [];
  let activeIndex = 0;
  for (let i = 0; i < t.length; i++) {
    if (cartTotal >= t[i].minCartTotal) activeIndex = i;
  }
  return activeIndex;
}

function formatDeliveryTierLabel({
  index,
  minCartTotal,
  nextMinCartTotal,
  tiersLength,
}) {
  if (tiersLength <= 1) return "עלות משלוח";
  if (index === 0 && nextMinCartTotal != null) {
    return ` עד ₪${nextMinCartTotal}`;
  }
  if (nextMinCartTotal != null) {
    return ` בין ₪${minCartTotal} ל-₪${nextMinCartTotal}`;
  }
  return `מעל ₪${minCartTotal}`;
}

/* ─── Re-use CartDrawer's image helper ───────────────────── */
function cartLineImageSrc(product) {
  const path = getDefaultMediaPath(product?.media);
  return path
    ? `${newGlobalFileServer}${path}?width=80&height=80`
    : `${newGlobalFileServer}placeholder.jpg`;
}

/* ─── Checkmark SVG ──────────────────────────────────────── */
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    width="13"
    height="13"
    aria-hidden
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ─── Deferred delivery fee notice ─────────────────────── */
const DeferredDeliveryNotice = ({ fee }) => {
  if (!fee || fee <= 0) return null;
  return (
    <div className="co-delivery-notice" role="note">
      דמי משלוח בסך <strong>₪{Number(fee).toFixed(2)}</strong> אינם נגבים כעת —
      התשלום יבוצע ישירות לשליח בעת קבלת המשלוח.
    </div>
  );
};

/* ─── Delivery tiers info + hover table ─────────────────── */
const DeliveryTiersInfoPopover = ({ tiers, activeIndex }) => {
  if (!tiers?.length) return null;

  return (
    <div className="co-delivery-tiers__info">
      <p className="co-delivery-tiers__info-line">
        דמי משלוח נקבעים לפי{" "}
        <span
          className="co-delivery-tiers__info-trigger"
          tabIndex={0}
          role="button"
          aria-label="הצגת מחירי משלוח לפי סכום עגלה"
        >
          סכום העגלה
        </span>
      </p>
      <div className="co-delivery-tiers__popover" role="tooltip">
        <table className="co-delivery-tiers__table">
          <thead>
            <tr>
              <th scope="col">סכום עגלה</th>
              <th scope="col">דמי משלוח</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier, index) => {
              const nextMinCartTotal = tiers[index + 1]?.minCartTotal;
              return (
                <tr
                  key={tier.minCartTotal}
                  className={
                    index === activeIndex
                      ? "co-delivery-tiers__table-row--active"
                      : ""
                  }
                >
                  <td>
                    {formatDeliveryTierLabel({
                      index,
                      minCartTotal: tier.minCartTotal,
                      nextMinCartTotal,
                      tiersLength: tiers.length,
                    })}
                  </td>
                  <td>₪{tier.price.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── Dynamic delivery pricing tiers banner ────────────── */
const DeliveryPricingTiersBanner = ({
  cartTotal,
  deliveryPrice,
  deliveryAvailable,
  cartApiPayload,
  variant = "sidebar",
}) => {
  if (!deliveryAvailable) return null;

  const currentPrice = toFiniteNumber(deliveryPrice);
  const serverTiers = extractDeliveryTiers(cartApiPayload);
  const tiersForDisplay = serverTiers
    ? serverTiers.slice().sort((a, b) => a.minCartTotal - b.minCartTotal)
    : null;

  if (tiersForDisplay?.length) {
    const activeIndex = getActiveDeliveryTierIndex(cartTotal, tiersForDisplay);
    const nextTier = tiersForDisplay[activeIndex + 1] ?? null;
    const activeTier = tiersForDisplay[activeIndex] ?? null;
    const displayPrice = currentPrice ?? activeTier?.price;
    if (displayPrice == null) return null;

    const nextThreshold = toFiniteNumber(nextTier?.minCartTotal);
    const amountToNext =
      nextThreshold != null ? Math.max(0, nextThreshold - cartTotal) : 0;
    const isFreeNext = nextTier && Number(nextTier.price) === 0;
    const showCheaperShippingTip =
      nextTier && amountToNext > 0 && amountToNext <= 20;

    return (
      <div
        className={`co-delivery-tiers co-delivery-tiers--${variant}`}
        role="note"
        aria-label="דמי משלוח"
      >
        <DeliveryTiersInfoPopover
          tiers={tiersForDisplay}
          activeIndex={activeIndex}
        />
        <div className="co-delivery-tiers__rows">
          <div className="co-delivery-tiers__row co-delivery-tiers__row--current">
            <span className="co-delivery-tiers__label">דמי משלוח</span>
            <span className="co-delivery-tiers__value">
              ₪{Number(displayPrice).toFixed(2)}
            </span>
          </div>
        </div>
        {showCheaperShippingTip ? (
          <p className="co-delivery-tiers__tip">
            עוד <strong>₪{amountToNext.toFixed(2)}</strong>
            {isFreeNext ? (
              <>
                {" "}
                למשלוח <strong>חינם</strong>
              </>
            ) : (
              <>
                {" "}
                למשלוח זול יותר (
                <strong>₪{Number(nextTier.price).toFixed(2)}</strong>)
              </>
            )}
          </p>
        ) : null}
      </div>
    );
  }

  if (currentPrice == null) return null;

  return (
    <div
      className={`co-delivery-tiers co-delivery-tiers--${variant}`}
      role="note"
      aria-label="דמי משלוח"
    >
      <div className="co-delivery-tiers__rows">
        <div className="co-delivery-tiers__row co-delivery-tiers__row--current">
          <span className="co-delivery-tiers__label">דמי משלוח</span>
          <span className="co-delivery-tiers__value">
            ₪{currentPrice.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ─── Lock SVG ───────────────────────────────────────────── */
const LockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* ─── Payment status overlay (loading / success / error) ─── */
const PaymentStatusScreen = ({
  status,
  onRetry,
  onHome,
  errorMessage,
  errorCategory,
  minCheckoutChargeMessage,
}) => {
  useEffect(() => {
    if (status === "success") {
      const t = setTimeout(onHome, 3000);
      return () => clearTimeout(t);
    }
  }, [status, onHome]);

  const content = (
    <div
      className="pss-overlay"
      role="dialog"
      aria-modal="true"
      aria-live="polite"
    >
      <div className="pss-card">
        {status === "loading" && (
          <>
            <div className="pss-loading-row">
              <div className="pss-spinner" aria-hidden />
              <p className="pss-title">מעבד את התשלום...</p>
            </div>
            <p className="pss-sub">אנא המתן, אל תסגור את הדף</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="pss-icon pss-icon--success">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                width="40"
                height="40"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11.5 14.5 15 10" strokeWidth="3" />
              </svg>
            </div>
            <p className="pss-title">ההזמנה התקבלה בהצלחה!</p>
            <p className="pss-sub">מעביר אותך לדף הבית...</p>
            <div className="pss-progress-bar">
              <div className="pss-progress-fill" />
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <div className="pss-icon pss-icon--error">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                width="40"
                height="40"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <p className="pss-title">
              {getCheckoutErrorTitle(
                errorCategory,
                errorMessage,
                minCheckoutChargeMessage,
              )}
            </p>
            <p className="pss-sub">
              {errorMessage ||
                getCheckoutErrorFallback(
                  errorCategory || CHECKOUT_ERROR_CATEGORY.PAYMENT,
                )}
            </p>
            <button className="pss-retry" onClick={onRetry}>
              {errorCategory === CHECKOUT_ERROR_CATEGORY.PROFILE ||
              errorCategory === CHECKOUT_ERROR_CATEGORY.ADDRESS
                ? "חזור לתיקון הפרטים"
                : "נסה שוב"}
            </button>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

/* ════════════════════════════════════════════════════════════
   ShopCart
════════════════════════════════════════════════════════════ */
const ShopCart = () => {
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const user = useSelector((state) => state.user.user);
  const stationId = useSelector((state) => state.user.stationId);
  const productsInCart = useSelector(
    (state) => state.productsInCart.productsInCart,
  );
  const orderSum = useSelector((state) => state.productsInCart.orderSum);
  const totalSum = useSelector((state) => state.productsInCart.totalSum);
  const deliveryPrice = useSelector(
    (state) => state.productsInCart.deliveryPrice,
  );
  const deliveryAvailable = useSelector(
    (state) => state.productsInCart.deliveryAvailable,
  );
  const cartApiPayload = useSelector(
    (state) => state.productsInCart.cartApiPayload,
  );
  const minCheckoutChargeAmount = useSelector(
    (state) => state.productsInCart.minCheckoutChargeAmount,
  );

  const navigate = useNavigate();
  const location = useLocation();
  const [allSum, setAllSum] = useState(0);
  const [toPayPopup, setToPayPopup] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  // null = idle, "loading" = processing, "success" = done, "error" = failed
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
  const [paymentErrorCategory, setPaymentErrorCategory] = useState(null);
  const [station, setStation] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(0);
  const [displayShipping, setDisplayShipping] = useState(0);
  const [orderCompleteSummary, setOrderCompleteSummary] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCouponCode, setAppliedCouponCode] = useState(null);
  const [comment, setComment] = useState("");
  const [deliveryPricingTiersFromApi, setDeliveryPricingTiersFromApi] =
    useState(null);
  const isCheckoutBlockedRef = useRef(false);
  const hasCheckoutRequestStartedRef = useRef(false);
  const splitPaymentsPayRef = useRef(null);
  const [isCheckoutInProgress, setIsCheckoutInProgress] = useState(false);

  const closePayPopup = useCallback(
    (succ, cardId, numPaymentSelect, responseData) => {
      splitPaymentsPayRef.current?.(
        succ,
        cardId,
        numPaymentSelect,
        responseData,
      );
    },
    [],
  );

  const requiresAgeConfirmation = useMemo(
    () => cartRequiresAgeConfirmation(getActiveCartItems(productsInCart)),
    [productsInCart],
  );

  const checkoutSteps = useMemo(
    () => getCheckoutSteps(requiresAgeConfirmation),
    [requiresAgeConfirmation],
  );

  /* ── NEW: single UI-only step state ── */
  const [currentStep, setCurrentStep] = useState(CHECKOUT_STEP.DETAILS);

  useEffect(() => {
    if (!isCouponCheckoutReturn(location)) return;

    const step = parseCheckoutReturnStep(location) ?? CHECKOUT_STEP.DETAILS;
    // Skip confirm step when returning without age-restricted items.
    if (step === CHECKOUT_STEP.CONFIRM && !requiresAgeConfirmation) {
      setCurrentStep(CHECKOUT_STEP.DETAILS);
    } else {
      setCurrentStep(step);
    }
    clearCouponReturn();
  }, [location, requiresAgeConfirmation]);

  useEffect(() => {
    if (!requiresAgeConfirmation && currentStep === CHECKOUT_STEP.CONFIRM) {
      setCurrentStep(CHECKOUT_STEP.DETAILS);
    }
    if (!requiresAgeConfirmation) {
      setAgeConfirmed(false);
    }
  }, [requiresAgeConfirmation, currentStep]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [toPayPopup, currentStep]);

  /* ── Editable user details (local, for this checkout session) ── */
  const [localFirstName, setLocalFirstName] = useState(user.firstName || "");
  const [localLastName, setLocalLastName] = useState(user.lastName || "");
  const [localPhone, setLocalPhone] = useState(user.phone || user.Phone || "");
  const [localEmail] = useState(user.email || "");
  const [editingDetails, setEditingDetails] = useState(false);

  /* ── Editable shipping address (local) — coerce to string ── */
  const initialStreetCity = getExistingStreetCity(user);
  const [localStreet, setLocalStreet] = useState(
    String(user.addrStreet || initialStreetCity?.streetName || ""),
  );
  const [localHouse, setLocalHouse] = useState(String(user.addrHouse || ""));
  const [localFloor, setLocalFloor] = useState(String(user.addrFloor || ""));
  const [localApt, setLocalApt] = useState(String(user.addrApartment || ""));
  const [localCity, setLocalCity] = useState(
    String(user.addrCity || user.City || initialStreetCity?.city || ""),
  );
  const [streetCityInput, setStreetCityInput] = useState(
    initialStreetCity?.query ?? "",
  );
  const [locationValidated, setLocationValidated] = useState(
    Boolean(initialStreetCity),
  );
  const [streetCityError, setStreetCityError] = useState("");
  const [localRecipientName, setLocalRecipientName] = useState(
    String(user.addrOrderRecipientName || ""),
  );
  const [editingAddress, setEditingAddress] = useState(
    !(user.addrStreet || user.addrCity),
  );
  const [confirmDeleteAddr, setConfirmDeleteAddr] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showListPicker, setShowListPicker] = useState(false);

  const getStation = useCallback(() => {
    getStationToCustomer(stationId).then((x) => setStation(x.data));
  }, [stationId]);

  useEffect(() => {
    const productsTotal = parseFloat(orderSum) + parseFloat(totalSum);
    const minFreeDelivery = station?.MinFreeDeleviry;
    const shouldChargeShipping =
      minFreeDelivery == null || productsTotal < Number(minFreeDelivery);
    const shipping = shouldChargeShipping ? Number(deliveryPrice || 0) : 0;
    setDisplayShipping(shipping);
    setAllSum(productsTotal);
    const finalTotal = productsTotal - Number(couponDiscount || 0);
    setPaymentOrder(Math.max(0, finalTotal));
  }, [totalSum, station, orderSum, deliveryPrice, couponDiscount]);

  useEffect(() => {
    getStation();
  }, [getStation]);

  useEffect(() => {
    if (!stationId) return undefined;
    let cancelled = false;
    getDeliveryPrices(stationId)
      .then((tiers) => {
        if (cancelled) return;
        setDeliveryPricingTiersFromApi(tiers);
      })
      .catch((err) => {
        console.error("Error loading delivery prices:", err);
        if (!cancelled) setDeliveryPricingTiersFromApi(null);
      });
    return () => {
      cancelled = true;
    };
  }, [stationId]);

  const cartApiPayloadWithDeliveryTiers = deliveryPricingTiersFromApi?.length
    ? {
        ...(cartApiPayload || {}),
        deliveryPricingTiers: deliveryPricingTiersFromApi,
      }
    : cartApiPayload;

  /* ── Hide header "לתשלום" button while on checkout page ── */
  useEffect(() => {
    document.body.classList.add("in-checkout");
    return () => document.body.classList.remove("in-checkout");
  }, []);

  const handleCouponApplied = (discount, couponCode) => {
    setCouponDiscount(discount);
    setAppliedCouponCode(couponCode);
    dispatch({ type: actionType.SET_COUPON_DISCOUNT, payload: discount });
  };

  const releaseCheckoutLock = () => {
    isCheckoutBlockedRef.current = false;
    releaseCheckoutRequest(hasCheckoutRequestStartedRef);
    setIsCheckoutInProgress(false);
  };

  /* ── Delete item from cart (mirrors CartDrawer.handleDelete) ── */
  const handleDelete = (line) => {
    const source = line.item;
    dispatch(
      upsertCartItem(
        source.productId,
        parseFloat(source.unitPrice).toFixed(2),
        totalQuantityAfterRemovingPackageLine(source, line.lineKind),
        String(stationId),
        getCartLineUpsertPromoId(source.product, source.promoId),
        source.product,
      ),
    );
  };

  const showCheckoutSuccess = ({ total = paymentOrder, orderNumber } = {}) => {
    setOrderCompleteSummary({
      itemCount: productsInCart.filter((p) => p.quantity > 0).length,
      total,
      deliveryFee: displayShipping,
      orderNumber,
    });
    setPaymentStatus(null);
    setCurrentStep(CHECKOUT_STEP.SUCCESS);
    setToPayPopup(false);
    dispatch(clearCart());
    releaseCheckoutLock();
  };

  // Checkout hit a 404 "cart not found": the server cart was already closed,
  // usually by an earlier checkout that succeeded after the browser dropped
  // the response. If a fresh order matching this checkout's amount exists,
  // show the success screen instead of a payment-failed dialog; otherwise
  // resync the stale local cart.
  const recoverFromStaleCart = async () => {
    try {
      const orderLookup = orderPerCustomer(user.id, stationId);
      const timeout = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Order lookup timed out")),
          STALE_CART_ORDER_LOOKUP_TIMEOUT_MS,
        ),
      );
      const { data } = await Promise.race([orderLookup, timeout]);
      const recentOrder = findMatchingRecentOrder(data?.orders, {
        expectedTotal: Number(paymentOrder),
      });
      if (recentOrder) {
        showCheckoutSuccess({
          total: getOrderChargedTotal(recentOrder) ?? paymentOrder,
          orderNumber: recentOrder.orderNumber,
        });
        return true;
      }
      dispatch(getCart(stationId));
    } catch (recoveryError) {
      console.error("Stale cart recovery failed:", recoveryError);
    }
    return false;
  };

  const splitPaymentsPay = (
    _succ,
    _PaymentMataraTokenId,
    NumPayment,
    responseData,
  ) => {
    if (!_succ) {
      setToPayPopup(false);
      if (!isCheckoutRequestInFlight(hasCheckoutRequestStartedRef)) {
        releaseCheckoutLock();
      }
      return;
    }

    if (!tryAcquireCheckoutRequest(hasCheckoutRequestStartedRef)) return;

    setToPayPopup(false);
    setPaymentStatus("loading");
    setPaymentErrorMessage("");
    setPaymentErrorCategory(null);

    const physicalAddress = [
      localStreet,
      localHouse,
      localFloor ? `קומה ${localFloor}` : null,
      localApt ? `דירה ${localApt}` : null,
    ]
      .filter(Boolean)
      .join(", ");
    const fullAddress = [localRecipientName || localLastName, physicalAddress]
      .filter(Boolean)
      .join(", ");
    const params = { ...responseData, address: fullAddress };

    checkout(params, stationId)
      .then(async (res) => {
        dispatch({ type: "CHECKOUT_SUCCESS", payload: res.data });
        const orderData = res.data;
        let base64Result = "";

        try {
          const element = document.createElement("div");
          element.style.width = "190mm";
          element.innerHTML = OrderReceiptTemplate(
            orderData,
            {
              ...user,
              firstName: localFirstName,
              lastName: localLastName,
              phone: localPhone,
            },
            fullAddress,
            paymentOrder,
            productsInCart,
            displayShipping,
          );
          const opt = {
            margin: [10, 5, 20, 5],
            filename: `order_${orderData.id}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { scale: 3, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          };
          const worker = html2pdf()
            .set(opt)
            .from(element)
            .toPdf()
            .get("pdf")
            .then(function (pdf) {
              const totalPages = pdf.internal.getNumberOfPages();
              for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(10);
                pdf.text(i + " / " + totalPages, 105, 285, { align: "center" });
              }
            });

          const pdfArrayBuffer = await worker.output("arraybuffer");
          const bytes = new Uint8Array(pdfArrayBuffer);
          let binary = "";
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          base64Result = btoa(binary);
        } catch (pdfErr) {
          console.error("PDF generation error:", pdfErr);
        }

        const mailPayload = {
          OrderWebId: orderData.orderNumber,
          name: `${localFirstName} ${localLastName}`.trim(),
          email: localEmail || user.email,
          address: fullAddress,
          customerId: user.id,
          amount: paymentOrder,
          updated: new Date().toLocaleDateString("he-IL"),
          Digit4: orderData?.payment?.lastFour || "****",
          PdfBase64: base64Result,
        };

        setToPayPopup(false);

        await axios.post(`${awsUrl}/v1/orders`, mailPayload, {
          headers: { Authorization: `bearer ${localStorage.jwtToken}` },
        });

        showCheckoutSuccess({ orderNumber: orderData.orderNumber });
      })
      .catch(async (error) => {
        const serverData = error?.response?.data;
        const isNetworkError = !error?.response;
        const rawMessage = serverData?.error || serverData?.message || "";

        // The cart no longer exists server-side (OPEN cart already closed).
        // A previous checkout attempt likely succeeded without the browser
        // registering it — verify against the order history before showing
        // a payment-failed dialog that invites a retry.
        if (isStaleCartCheckoutError(error?.response?.status, rawMessage)) {
          const recovered = await recoverFromStaleCart();
          if (recovered) return;
        }

        const resolved = resolveCheckoutError(rawMessage, {
          isNetworkError,
          minCheckoutChargeAmount,
        });
        setPaymentErrorMessage(resolved.message);
        setPaymentErrorCategory(resolved.category);
        setPaymentStatus("error");
        setToPayPopup(false);
        releaseCheckoutLock();
        console.error("orderPost Error:", error, "server payload:", serverData);
      });
  };
  splitPaymentsPayRef.current = splitPaymentsPay;

  const toPay = () => {
    if (isCheckoutBlockedRef.current) return;

    if (guardCustomerAction(dispatch, { user: userState })) {
      return;
    }

    if (requiresAgeConfirmation && !ageConfirmed) {
      error("יש לאשר כי הינך מעל גיל 18 לפני ביצוע ההזמנה", "error");
      return;
    }

    if (!deliveryAvailable) {
      error("לא ניתן לבצע הזמנה במחיר זה", "error");
      return;
    }

    if (isBelowMinCheckoutCharge(paymentOrder, minCheckoutChargeAmount)) {
      error(getMinCheckoutChargeMessage(minCheckoutChargeAmount), "error");
      return;
    }

    const products = getActiveCartItems(productsInCart);
    if (!products.length) {
      warnEmptyCart();
      return;
    }

    const sumBeforeDiscount = parseFloat(totalSum) + parseFloat(orderSum);
    if (sumBeforeDiscount >= station?.MinPrice || station?.MinPrice == null) {
      const validation = validateCheckoutDetails(getCheckoutDetailsFields(), {
        requireAddress: true,
      });
      if (!validation.valid) {
        showCheckoutValidationIssue(validation);
        return;
      }

      isCheckoutBlockedRef.current = true;
      setIsCheckoutInProgress(true);
      setToPayPopup(true);
    } else {
      error("מינימום הזמנה: " + station?.MinPrice + " ש״ח", "error");
    }
  };

  const error = (textError, type) => {
    showAlert({ msgTitle: textError, msgType: type });
  };

  /* ── Step navigation ── */
  const goBack = () =>
    setCurrentStep((s) => Math.max(s - 1, CHECKOUT_STEP_MIN));
  const handleCheckoutBack = () => {
    if (toPayPopup) {
      closePayPopup(false);
      return;
    }
    if (currentStep > 0) {
      goBack();
      return;
    }
    navigate("/");
  };

  /* ── Derived values ── */
  const displayLines = expandCartItemsToPackageLines(productsInCart);
  const minCheckoutChargeMessage = getMinCheckoutChargeMessage(
    minCheckoutChargeAmount,
  );
  const belowMinCheckoutCharge = isBelowMinCheckoutCharge(
    paymentOrder,
    minCheckoutChargeAmount,
  );
  const userInitials = [localFirstName, localLastName]
    .filter(Boolean)
    .map((n) => String(n)[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);

  const preventNonDigitKey = (e) => {
    if (
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
      ].includes(e.key)
    ) {
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  /* Build the exact payload shape the API expects — all values coerced to strings */
  const str = (v) => String(v ?? "");
  const buildUpdatePayload = ({
    firstName,
    lastName,
    phone,
    city,
    street,
    house,
    floor,
    apt,
    zip,
    orderRecipientName,
  }) => {
    return {
      defaultLocation: str(user.defaultLocation || stationId),
      firstName: str(firstName),
      lastName: str(lastName),
      phone: str(phone),
      addrCity: str(city),
      addrStreet: str(street),
      addrHouse: str(house),
      addrFloor: str(floor),
      addrApartment: str(apt),
      addrZipCode: str(zip),
      addrOrderRecipientName: str(orderRecipientName).trim(),
    };
  };

  /* Validate required fields before PUT — returns true if OK */
  const getCheckoutDetailsFields = () => ({
    firstName: localFirstName,
    lastName: localLastName,
    phone: localPhone,
    city: localCity,
    street: localStreet,
    house: localHouse,
    locationValidated,
  });

  const showCheckoutValidationIssue = (validation) => {
    showAlert({
      msgTitle: validation.message,
      msgType: "error",
    });

    if (validation.category === CHECKOUT_VALIDATION_CATEGORY.PROFILE) {
      setEditingDetails(true);
      setCurrentStep(CHECKOUT_STEP.DETAILS);
      return;
    }

    if (validation.field === CHECKOUT_FIELD.ADDRESS) {
      setStreetCityError("חובה להכניס רחוב ועיר");
    }
    setEditingAddress(true);
    setCurrentStep(CHECKOUT_STEP.DETAILS);
  };

  const validateBeforeSave = ({ requireAddress = true } = {}) => {
    const validation = validateCheckoutDetails(getCheckoutDetailsFields(), {
      requireAddress,
    });
    if (!validation.valid) {
      showCheckoutValidationIssue(validation);
      return false;
    }
    return true;
  };

  const saveDetails = async () => {
    if (!validateBeforeSave({ requireAddress: false })) return;
    setIsSavingDetails(true);
    try {
      const payload = buildUpdatePayload({
        firstName: localFirstName,
        lastName: localLastName,
        phone: localPhone,
        city: localCity || user.addrCity || user.City || "",
        street: localStreet || user.addrStreet || "",
        house: localHouse || user.addrHouse || "",
        floor: localFloor || user.addrFloor || "",
        apt: localApt || user.addrApartment || "",
        zip: user.addrZipCode || "",
        orderRecipientName:
          localRecipientName || user.addrOrderRecipientName || "",
      });
      await UpdateUser(payload);
      dispatch({
        type: "UPDATE_USER",
        payload: {
          ...payload,
          addrOrderRecipientName: payload.addrOrderRecipientName || "",
        },
      });
      showAlert({ msgTitle: "הפרטים עודכנו בהצלחה", msgType: "success" });
      setEditingDetails(false);
    } catch (err) {
      console.error("saveDetails error:", err);
      const serverData = err?.response?.data;
      const resolved = resolveCheckoutError(
        serverData?.error || serverData?.message || "",
        { isNetworkError: !err?.response, minCheckoutChargeAmount },
      );
      showAlert({
        msgTitle: resolved.message,
        msgType: "error",
      });
    } finally {
      setIsSavingDetails(false);
    }
  };

  const resetStreetCitySelection = (clearInput = false) => {
    setLocationValidated(false);
    setStreetCityError("");
    setLocalStreet("");
    setLocalCity("");
    if (clearInput) {
      setStreetCityInput("");
    }
  };

  const handleCartPlaceSelected = (place) => {
    if (!place?.geometry) {
      showAlert({
        msgTitle: "אנא הזינו את שם הרחוב והעיר ובחרו מתוך האפשרויות המוצעות",
        msgTimer: "3000",
        msgType: "info",
      });
      return;
    }

    const parsed = parseAddressFromPlace(place);
    if (!parsed.streetName || !parsed.city) {
      showAlert({
        msgTitle: "אנא הזינו את שם הרחוב ועיר המגורים",
        msgType: "error",
      });
      return;
    }

    setStreetCityInput(`${parsed.streetName}, ${parsed.city}`);
    setLocalStreet(parsed.streetName);
    setLocalCity(parsed.city);
    if (parsed.streetNumber) {
      setLocalHouse(parsed.streetNumber);
    }
    setStreetCityError("");
    setLocationValidated(true);
  };

  const handleDeleteAddress = () => {
    setLocalStreet("");
    setLocalHouse("");
    setLocalFloor("");
    setLocalApt("");
    setLocalCity("");
    setStreetCityInput("");
    setLocationValidated(false);
    setStreetCityError("");
    setLocalRecipientName("");
    setConfirmDeleteAddr(false);
    setEditingAddress(true);
  };

  const goNextFromShipping = async () => {
    if (!validateBeforeSave()) return;
    if (editingAddress) {
      setIsSavingAddress(true);
      try {
        const payload = buildUpdatePayload({
          firstName: localFirstName,
          lastName: localLastName,
          phone: localPhone,
          city: localCity,
          street: localStreet,
          house: localHouse,
          floor: localFloor,
          apt: localApt,
          zip: user.addrZipCode || "",
          orderRecipientName: localRecipientName,
        });
        await UpdateUser(payload);
        dispatch({
          type: "UPDATE_USER",
          payload: {
            ...payload,
            addrOrderRecipientName: payload.addrOrderRecipientName || "",
          },
        });
        setEditingAddress(false);
      } catch (err) {
        console.error("saveAddress error:", err);
        const serverData = err?.response?.data;
        const resolved = resolveCheckoutError(
          serverData?.error || serverData?.message || "",
          { isNetworkError: !err?.response, minCheckoutChargeAmount },
        );
        showAlert({
          msgTitle: resolved.message,
          msgType: "error",
        });
        return;
      } finally {
        setIsSavingAddress(false);
      }
    }
    if (requiresAgeConfirmation) {
      setCurrentStep(CHECKOUT_STEP.CONFIRM);
      return;
    }
    toPay();
  };

  const localAddressDisplay = formatAddressLines({
    addrOrderRecipientName: localRecipientName,
    lastName: localLastName,
    streetName: localStreet,
    houseNumber: localHouse,
    floor: localFloor,
    apartment: localApt,
    city: localCity,
  });

  const isPaymentOpen =
    toPayPopup || paymentStatus === "loading" || paymentStatus === "error";
  const stepDisplayIndex = getCheckoutStepDisplayIndex(currentStep, {
    requiresAgeConfirmation,
    isPaymentOpen,
  });

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <div className="checkout-page" dir="rtl">
      {/* ── FIX 1: Stepper is FIRST child, full width, above all columns ── */}
      <div className="co-stepper" aria-label="שלבי ההזמנה">
        <div className="co-stepper__track">
          {checkoutSteps.map((label, idx) => {
            const isDone = idx < stepDisplayIndex;
            const isActive = idx === stepDisplayIndex;
            return (
              <div
                key={`${label}-${idx}`}
                className={[
                  "cstep",
                  isActive ? "cstep--active" : "",
                  isDone ? "cstep--done" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {idx > 0 && (
                  <div
                    className={`cstep__line${stepDisplayIndex >= idx ? " cstep__line--done" : ""}`}
                  />
                )}
                <div
                  className="cstep__circle"
                  aria-current={isActive ? "step" : undefined}
                >
                  {isDone ? <CheckIcon /> : <span>{idx + 1}</span>}
                </div>
                <span className="cstep__label">{label}</span>
              </div>
            );
          })}
        </div>
        {/* Mobile compact indicator */}
        <div className="co-stepper__mobile">
          <span className="cstep-mob__name">
            {checkoutSteps[stepDisplayIndex]}
          </span>
          <span className="cstep-mob__count">
            שלב {stepDisplayIndex + 1} מתוך {checkoutSteps.length}
          </span>
        </div>
      </div>

      {/* ── FIX 1: co-body holds the two columns ── */}
      <div
        className={`co-body${currentStep === CHECKOUT_STEP.SUCCESS ? " co-body--success" : ""}`}
      >
        {/* ── RIGHT column: one step at a time ── */}
        <div className="co-main">
          {/* ═══ STEP 0: פרטי לקוח ═══ */}
          {currentStep === CHECKOUT_STEP.DETAILS && !toPayPopup && (
            <div className="co-step-pane">
              {/* User header card */}
              <div className="co-s1-user-card">
                <div className="co-s1-avatar">{userInitials || "?"}</div>
                <div className="co-s1-user-info">
                  <span className="co-s1-name">
                    {localFirstName} {localLastName}
                  </span>
                  <span className="co-s1-email">{localEmail}</span>
                </div>
              </div>

              {/* Customer details — view / edit toggle */}
              <div className="co-card">
                <div className="co-card__head co-card__head--spread">
                  <div className="co-card__head-left">
                    <span className="co-card__title">פרטי לקוח</span>
                  </div>
                  {editingDetails ? (
                    <button
                      type="button"
                      className={`co-confirm-btn${isSavingDetails ? " co-confirm-btn--loading" : ""}`}
                      onClick={saveDetails}
                      disabled={isSavingDetails}
                      aria-label="שמור פרטים"
                    >
                      {isSavingDetails ? (
                        <span className="co-confirm-btn__spinner" aria-hidden />
                      ) : (
                        <Check size={12} strokeWidth={2.5} aria-hidden />
                      )}
                      {isSavingDetails ? "שומר..." : "שמור"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="co-icon-btn"
                      onClick={() => setEditingDetails(true)}
                      aria-label="ערוך פרטי לקוח"
                      title="ערוך פרטים"
                    >
                      <Pencil size={13} strokeWidth={2} aria-hidden />
                    </button>
                  )}
                </div>

                <div className="co-card__body">
                  {editingDetails ? (
                    /* ── Edit mode ── */
                    <>
                      <div className="co-field-row">
                        <div className="co-field">
                          <label className="co-label">שם פרטי</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localFirstName}
                            onChange={(e) => setLocalFirstName(e.target.value)}
                            autoComplete="given-name"
                          />
                        </div>
                        <div className="co-field">
                          <label className="co-label">שם משפחה</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localLastName}
                            onChange={(e) => setLocalLastName(e.target.value)}
                            autoComplete="family-name"
                          />
                        </div>
                      </div>
                      <div className="co-field-row">
                        <div className="co-field">
                          <label className="co-label">טלפון</label>
                          <input
                            className="co-input"
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={localPhone}
                            onChange={(e) =>
                              setLocalPhone(
                                e.target.value.replace(/[^0-9]/g, ""),
                              )
                            }
                            autoComplete="tel"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    /* ── View mode ── */
                    <ul className="co-info-list">
                      {(localFirstName || localLastName) && (
                        <li className="co-info-item">
                          <User
                            size={13}
                            className="co-info-icon"
                            aria-hidden
                          />
                          <span>
                            {localFirstName} {localLastName}
                          </span>
                        </li>
                      )}
                      {localEmail && (
                        <li className="co-info-item">
                          <Mail
                            size={13}
                            className="co-info-icon"
                            aria-hidden
                          />
                          <span dir="ltr">{localEmail}</span>
                        </li>
                      )}
                      {localPhone && (
                        <li className="co-info-item">
                          <Phone
                            size={13}
                            className="co-info-icon"
                            aria-hidden
                          />
                          <span dir="ltr">{localPhone}</span>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>

              {/* Shipping address — view / edit / delete */}
              <div className="co-card">
                <div className="co-card__head co-card__head--spread">
                  <div className="co-card__head-left">
                    <span className="co-card__title">כתובת למשלוח</span>
                  </div>
                  <div className="co-card__head-actions">
                    {editingAddress ? null : (
                      <>
                        <button
                          type="button"
                          className="co-icon-btn"
                          onClick={() => setEditingAddress(true)}
                          aria-label="ערוך כתובת"
                          title="ערוך כתובת"
                        >
                          <Pencil size={13} strokeWidth={2} aria-hidden />
                        </button>
                        {!confirmDeleteAddr ? (
                          <button
                            type="button"
                            className="co-icon-btn co-icon-btn--danger"
                            onClick={() => setConfirmDeleteAddr(true)}
                            aria-label="מחק כתובת"
                            title="מחק כתובת"
                          >
                            <Trash2 size={13} strokeWidth={2} aria-hidden />
                          </button>
                        ) : (
                          <span className="co-delete-confirm">
                            <button
                              type="button"
                              className="co-delete-confirm__yes"
                              onClick={handleDeleteAddress}
                            >
                              מחק
                            </button>
                            <button
                              type="button"
                              className="co-delete-confirm__no"
                              onClick={() => setConfirmDeleteAddr(false)}
                            >
                              ביטול
                            </button>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="co-card__body">
                  {editingAddress ? (
                    /* ── Edit form ── */
                    <>
                      <div className="co-field">
                        <label className="co-label">שם מקבל ההזמנה</label>
                        <input
                          className="co-input"
                          type="text"
                          value={localRecipientName}
                          onChange={(e) =>
                            setLocalRecipientName(e.target.value)
                          }
                          placeholder="שם משפחה/שם משרד/שם חברה/שם מוסד"
                          autoComplete="name"
                        />
                      </div>
                      <div className="co-field">
                        <label
                          className="co-label"
                          htmlFor="cart-address-search"
                        >
                          רחוב ועיר*
                        </label>
                        <Autocomplete
                          id="cart-address-search"
                          className="co-input"
                          value={streetCityInput}
                          options={{
                            componentRestrictions: { country: "ISR" },
                            types: ["geocode"],
                          }}
                          onPlaceSelected={handleCartPlaceSelected}
                          onChange={(e) => {
                            setStreetCityInput(e.target.value);
                            setStreetCityError("");
                            if (locationValidated) {
                              resetStreetCitySelection();
                            }
                          }}
                          placeholder="הזינו רחוב ועיר ובחרו מתוך האפשרויות המוצעות"
                        />
                        {streetCityError ? (
                          <span className="co-field-error">
                            {streetCityError}
                          </span>
                        ) : null}
                      </div>
                      <div className="co-field-row co-field-row--3">
                        <div className="co-field">
                          <label className="co-label">מספר בית*</label>
                          <input
                            className="co-input"
                            type="tel"
                            inputMode="numeric"
                            value={localHouse}
                            onKeyDown={preventNonDigitKey}
                            onChange={(e) =>
                              setLocalHouse(stripToDigits(e.target.value))
                            }
                            placeholder="10"
                          />
                        </div>
                        <div className="co-field">
                          <label className="co-label">קומה</label>
                          <input
                            className="co-input"
                            type="text"
                            inputMode="text"
                            value={localFloor}
                            onChange={(e) =>
                              setLocalFloor(
                                stripToSignedInteger(e.target.value),
                              )
                            }
                            placeholder="1"
                          />
                        </div>
                        <div className="co-field">
                          <label className="co-label">דירה</label>
                          <input
                            className="co-input"
                            type="tel"
                            inputMode="numeric"
                            value={localApt}
                            onKeyDown={preventNonDigitKey}
                            onChange={(e) =>
                              setLocalApt(stripToDigits(e.target.value))
                            }
                            placeholder="1"
                          />
                        </div>
                      </div>
                    </>
                  ) : /* ── View mode ── */
                  localAddressDisplay.hasData ? (
                    <div className="co-addr-chip co-addr-chip--selected">
                      <div className="co-addr-icon" aria-hidden>
                        <MapPin size={18} strokeWidth={2} />
                      </div>
                      <div className="co-addr-body">
                        {localAddressDisplay.name ? (
                          <span className="co-addr-name">
                            {localAddressDisplay.name}
                          </span>
                        ) : null}
                        {localAddressDisplay.streetLine ? (
                          <span className="co-addr-line">
                            {localAddressDisplay.streetLine}
                          </span>
                        ) : null}
                        {localAddressDisplay.secondaryLine ? (
                          <span className="co-addr-line co-addr-line--muted">
                            {localAddressDisplay.secondaryLine}
                          </span>
                        ) : null}
                        {localPhone ? (
                          <span className="co-addr-line co-addr-line--muted">
                            {localPhone}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <p className="co-addr-empty">
                      לא הוגדרה כתובת — לחץ על עריכה להוספה
                    </p>
                  )}
                </div>
              </div>

              <div className="co-card">
                <div className="co-card__head">
                  <span className="co-card__title">הערה וקוד קופון</span>
                </div>
                <div className="co-card__body">
                  <textarea
                    className="co-textarea"
                    placeholder="הערה להזמנה"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <p className="co-comment-note">
                    * אין כל התחייבות למלא אחר הבקשה
                  </p>
                  <CouponCode
                    userId={user.id}
                    stationId={stationId}
                    orderSum={parseFloat(totalSum) + parseFloat(orderSum)}
                    onCouponApplied={handleCouponApplied}
                    checkoutStep={currentStep}
                  />
                </div>
              </div>

              <div className="co-step-nav">
                <button
                  type="button"
                  className="co-btn co-btn--back"
                  onClick={handleCheckoutBack}
                  disabled={isSavingAddress}
                >
                  <ChevronRight size={18} strokeWidth={2.5} aria-hidden />
                  חזור לחנות
                </button>
                <button
                  type="button"
                  className={`co-btn co-btn--primary${isSavingAddress ? " disabled" : ""}`}
                  onClick={goNextFromShipping}
                  disabled={isSavingAddress}
                >
                  {isSavingAddress
                    ? "שומר..."
                    : requiresAgeConfirmation
                      ? "המשך"
                      : "אשר והמשך לתשלום"}
                </button>
              </div>
            </div>
          )}

          {/* ═══ STEP 1 (optional): אישור גיל 18+ ═══ */}
          {requiresAgeConfirmation &&
            currentStep === CHECKOUT_STEP.CONFIRM &&
            !toPayPopup && (
              <div className="co-step-pane">
                <DeferredDeliveryNotice fee={displayShipping} />

                {belowMinCheckoutCharge ? (
                  <p className="order-summary__note" role="alert">
                    {minCheckoutChargeMessage}
                  </p>
                ) : null}

                <div className="co-card">
                  <div className="co-card__body">
                    <div className="checkbox-style">
                      <label>
                        <input
                          type="checkbox"
                          checked={ageConfirmed}
                          onChange={() => setAgeConfirmed((prev) => !prev)}
                        />
                        <span className="verify">
                          {" "}
                          ההזמנה כוללת מוצרים המיועדים לבגירים בלבד. הנני מאשר/ת
                          כי אני מעל גיל 18
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="co-step-nav">
                  <button
                    type="button"
                    className="co-btn co-btn--back"
                    onClick={handleCheckoutBack}
                  >
                    <ChevronRight size={18} strokeWidth={2.5} aria-hidden />
                    חזור
                  </button>
                  <button
                    type="button"
                    onClick={toPay}
                    className={`co-btn co-btn--primary ${isCheckoutInProgress || belowMinCheckoutCharge ? "disabled" : ""}`}
                    disabled={isCheckoutInProgress || belowMinCheckoutCharge}
                  >
                    אשר והמשך לתשלום
                    <ChevronLeft size={18} strokeWidth={2.5} aria-hidden />
                  </button>
                </div>
              </div>
            )}

          {/* ═══ PAYMENT: תשלום ═══ */}
          {toPayPopup ? (
            <div className="co-step-pane">
              <PayPopup
                embedded
                SumPay={paymentOrder}
                tranmode={true}
                deferredDeliveryFee={
                  displayShipping > 0 ? displayShipping : null
                }
                closePayPopup={closePayPopup}
                coupons={appliedCouponCode ? [appliedCouponCode] : []}
                comment={comment}
              />
              <div className="co-step-nav">
                <button
                  type="button"
                  className="co-btn co-btn--back"
                  onClick={handleCheckoutBack}
                >
                  <ChevronRight size={18} strokeWidth={2.5} aria-hidden />
                  חזור
                </button>
              </div>
            </div>
          ) : null}

          {/* ═══ SUCCESS: סיום ═══ */}
          {currentStep === CHECKOUT_STEP.SUCCESS && (
            <div className="co-step-pane co-success-pane">
              <div className="co-success-hero">
                <div className="co-success-icon" aria-hidden>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="56"
                    height="56"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="9 12 11.5 14.5 15 10" strokeWidth="2.5" />
                  </svg>
                </div>
                <h1 className="co-success-heading">ההזמנה בוצעה בהצלחה</h1>
                <p className="co-success-thanks">
                  שמחים שבחרת לקנות אצלנו – מחכים לראותך שוב!
                </p>
              </div>

              {orderCompleteSummary?.orderNumber ? (
                <div className="co-success-order">
                  <span className="co-success-order__label">
                    מספר ההזמנה שלך:
                  </span>
                  <span className="co-success-order__value">
                    {orderCompleteSummary.orderNumber}
                  </span>
                </div>
              ) : null}

              <p className="co-success-ship">נארוז ונשלח אותה במועד שביקשת.</p>

              <DeferredDeliveryNotice
                fee={orderCompleteSummary?.deliveryFee ?? displayShipping}
              />

              <div className="co-success-actions">
                <button
                  type="button"
                  className="co-btn co-btn--primary"
                  onClick={() => navigate("/history")}
                >
                  לצפייה בפרטי ההזמנה
                </button>
                <button
                  type="button"
                  className="co-btn co-btn--ghost"
                  onClick={() => navigate("/")}
                >
                  המשך לקניות
                </button>
              </div>

              <p className="co-success-status">
                ניתן לבדוק את סטטוס ההזמנה בכל עת ב־
                <NavLink className="co-success-status__link" to="/history">
                  אזור האישי
                </NavLink>
              </p>
            </div>
          )}
        </div>

        {/* ── LEFT column: order summary sidebar (hidden on success step) ── */}
        {currentStep !== 2 ? (
          <div className="co-sidebar">
            <div className="co-sidebar-card">
              {/* Sidebar header */}
              <div className="co-sidebar-head">
                <span className="co-sidebar-head__title">סיכום הזמנה</span>
                {displayLines.length > 0 && (
                  <span className="co-sidebar-head__count">
                    {displayLines.length} פריטים
                  </span>
                )}
              </div>

              {/* Cart items — uses CartDrawer CSS classes for identical appearance */}
              <div className="co-sidebar-list-wrap">
                <div className="cart-drawer__list co-sidebar-list">
                  {productsInCart.length === 0 ? (
                    <div className="co-empty-cart">
                      <p className="co-empty-cart__message">העגלה ריקה</p>
                      <button
                        type="button"
                        className="co-empty-cart__btn"
                        onClick={() => setShowListPicker(true)}
                      >
                        <ListChecks size={18} strokeWidth={2} aria-hidden />
                        בחר מרשימת קניות
                      </button>
                    </div>
                  ) : (
                    displayLines.map((line) => {
                      const element = line.item;
                      const img = cartLineImageSrc(element.product);
                      return (
                        <article
                          key={line.lineKey}
                          className={`cart-drawer-line cart-drawer-line--narrow${
                            line.isBundleLine ? " cart-drawer-line--bundle" : ""
                          }`}
                        >
                          <div className="cart-drawer-line__layout">
                            <span className="cart-drawer-line__thumb-btn">
                              <span className="cart-drawer-line__thumb-wrap">
                                <img
                                  loading="lazy"
                                  src={img}
                                  alt=""
                                  className="cart-drawer-line__thumb-img"
                                />
                              </span>
                            </span>
                            <div className="cart-drawer-line__content">
                              <div className="cart-drawer-line__top">
                                <span className="cart-drawer-line__title-block">
                                  <span className="cart-drawer-line__name">
                                    {element.product?.name}
                                  </span>
                                </span>
                                {pendingDeleteId === line.lineKey ? (
                                  <span className="co-delete-confirm co-delete-confirm--inline">
                                    <button
                                      type="button"
                                      className="co-delete-confirm__yes"
                                      onClick={() => {
                                        handleDelete(line);
                                        setPendingDeleteId(null);
                                      }}
                                    >
                                      הסר
                                    </button>
                                    <button
                                      type="button"
                                      className="co-delete-confirm__no"
                                      onClick={() => setPendingDeleteId(null)}
                                    >
                                      ביטול
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="cart-drawer-line__dismiss-header"
                                    aria-label="הסר מהעגלה"
                                    onClick={() =>
                                      setPendingDeleteId(line.lineKey)
                                    }
                                  >
                                    <X size={14} strokeWidth={2} aria-hidden />
                                  </button>
                                )}
                              </div>
                              {line.dealNote ? (
                                <div className="cart-drawer-line__deal-note">
                                  {line.dealNote}
                                </div>
                              ) : null}
                              <div className="cart-drawer-line__bottom">
                                <span className="cart-drawer-line__details-hit">
                                  <div className="cart-drawer-line__details-zone">
                                    <div className="cart-drawer-line__details-lines">
                                      <ProductInfoLine
                                        product={element.product}
                                      />
                                    </div>
                                  </div>
                                </span>
                                <div
                                  className="cart-drawer-line__qty-zone"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                >
                                  <div className="cart-drawer-line__qty">
                                    <ProductAddToCart
                                      element={element}
                                      haveInfo={false}
                                      retailCard
                                      lineKind={line.lineKind}
                                      onRequestRemove={() =>
                                        setPendingDeleteId(line.lineKey)
                                      }
                                    />
                                  </div>
                                </div>
                                <span className="cart-drawer-line__price-hit">
                                  <span className="cart-drawer-line__price-block">
                                    <span className="cart-drawer-line__price-num">
                                      {Number(line.lineTotal).toFixed(2)}
                                    </span>
                                    <span className="cart-drawer-line__price-sym">
                                      {" "}
                                      ₪
                                    </span>
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Price summary — uses CartDrawer's summary CSS classes */}
              <div className="cart-drawer__summary co-sidebar-summary">
                <div className="cart-drawer__summary-row">
                  <span>סיכום מוצרים</span>
                  <span>₪{allSum.toFixed(2)}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="cart-drawer__summary-row">
                    <span>הנחת קופון</span>
                    <span>-₪{Number(couponDiscount).toFixed(2)}</span>
                  </div>
                )}
                <div className="cart-drawer__summary-row cart-drawer__summary-row--grand">
                  <span>סה&quot;כ הזמנה</span>
                  <span>₪{paymentOrder.toFixed(2)}</span>
                </div>
                {belowMinCheckoutCharge ? (
                  <p className="order-summary__note" role="alert">
                    {minCheckoutChargeMessage}
                  </p>
                ) : null}
              </div>

              <DeliveryPricingTiersBanner
                cartTotal={allSum}
                deliveryPrice={deliveryPrice}
                deliveryAvailable={deliveryAvailable}
                cartApiPayload={cartApiPayloadWithDeliveryTiers}
              />

              {/* Security note */}
              <div className="co-secure-note">
                <LockIcon />
                <span>תשלום מאובטח ומוצפן</span>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Payment popup ── */}
      {paymentStatus === "loading" || paymentStatus === "error" ? (
        <PaymentStatusScreen
          status={paymentStatus}
          errorMessage={paymentErrorMessage}
          errorCategory={paymentErrorCategory}
          minCheckoutChargeMessage={minCheckoutChargeMessage}
          onRetry={() => {
            setPaymentStatus(null);
            setPaymentErrorMessage("");
            if (paymentErrorCategory === CHECKOUT_ERROR_CATEGORY.PROFILE) {
              setCurrentStep(CHECKOUT_STEP.DETAILS);
              setEditingDetails(true);
            } else if (
              paymentErrorCategory === CHECKOUT_ERROR_CATEGORY.ADDRESS
            ) {
              setCurrentStep(CHECKOUT_STEP.DETAILS);
              setEditingAddress(true);
            } else {
              setToPayPopup(true);
            }
            setPaymentErrorCategory(null);
          }}
          onHome={() => navigate("/")}
        />
      ) : null}

      {showListPicker ? (
        <PopUp
          width="480px"
          close={() => setShowListPicker(false)}
          wrapperClassName="popup-wrapper--shopping-list"
        >
          <CartShoppingListPicker close={() => setShowListPicker(false)} />
        </PopUp>
      ) : null}
    </div>
  );
};

export default ShopCart;
